import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  ActivityFilterKey,
  ActivityFilterModel,
  ActivityOptions,
  Severity,
  useActivityListFilter,
  useCurrentWorkspace,
  useSetActivityListFilter,
} from '@gripp/shared-logic';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Platform, Pressable, ScrollView, View } from 'react-native';
import { Checkbox, RadioButton, Text } from 'react-native-paper';
import { array, boolean, string, object as yupObject } from 'yup';
import { Colors } from '../../themes/colors';
import { useRoleHex } from '../../themes';
import { FormContext } from '../forms';
import { SeverityIcons } from '../issue/severityIcons';
import { DropdownFooter } from '../modals/dropdownFooter';
import { ApplyButton } from './applyButton';
import { FilterType } from './filterButton';
import { FilterHeader } from './filterHeader';
import { filterStyles as styles } from './filterStyle';
import { GroupFilter } from './groupFilter';
import { SwitchToggle } from './switchToggle';

interface ActivityFilterScreenProps {
  filterKey: ActivityFilterKey;
  goBack: () => void;
  type: FilterType;
}

export const ActivityFilter = ({
  filterKey,
  goBack,
  type,
}: ActivityFilterScreenProps) => {
  const { t } = useTranslation();
  const workspace = useCurrentWorkspace(true);
  // The checked colour of BOTH selection controls below. Paper runs it through
  // the `color` library (Checkbox/utils getAndroidSelectionControlColor ->
  // Color(checkedColor).fade(0.32), which RadioButton.Android imports too),
  // and that cannot parse a var() - it throws during render and takes the
  // panel down through the error boundary. Resolve to the mode's hex instead.
  // `rippleColor` does not avoid it: Checkbox.Item applies that to the row's
  // TouchableRipple and never forwards it to the inner Checkbox.
  // `uncheckedColor` is safe, returned as-is without any arithmetic.
  const selectionCheckedColor = useRoleHex('textStrong');
  const isWeb = Platform.OS === 'web';

  const activityOptions = [
    {
      value: ActivityOptions.EVERYTHING,
      label: t('activity.filter.everything'),
      icon: null,
    },
    {
      value: ActivityOptions.MENTIONED,
      label: t('activity.filter.onlyMentions'),
      icon: 'at',
    },
    {
      value: ActivityOptions.PINNED,
      label: t('activity.filter.onlyPinned'),
      icon: 'pin',
    },
    {
      value: ActivityOptions.SCHEDULED,
      label: t('activity.filter.onlyScheduled'),
      icon: 'alarm',
    },
    {
      value: ActivityOptions.NOT_SCHEDULED,
      label: t('activity.filter.notScheduled'),
      icon: 'alarm',
    },
  ];

  const { data: filter } = useActivityListFilter(filterKey);

  const { mutateAsync: setActivityListFilter } =
    useSetActivityListFilter(filterKey);

  const formSchema = yupObject({
    orderBy: string().optional().nullable(),
    severity: array().of(string()).optional().nullable(),
    assetName: string().optional().nullable(),
    groupIds: array().of(string()).optional().nullable(),
    labelIds: array().of(string()).optional().nullable(),
    isCombinedGroupsFilter: boolean().optional().nullable(),
    activityOption: string().optional().nullable(),
  });

  const formMethods = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    values: {
      orderBy: filter?.orderBy,
      severity: filter?.severity ?? Object.values(Severity),
      assetName: filter?.assetName,
      groupIds: filter?.groupIds,
      labelIds: filter?.labelIds,
      isCombinedGroupsFilter: filter?.isCombinedGroupsFilter,
      activityOption: filter?.activityOption ?? ActivityOptions.EVERYTHING,
    },
  });
  
  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
    watch,
  } = formMethods;

  const formData = watch();

  const isTypesFilter = type === FilterType.ACTIVITYTYPES;
  const isLabelsFilter = type === FilterType.ACTIVITYLABELS;
  const isGroupsFilter = type === FilterType.ACTIVITYGROUPS;

  const resetForm = useCallback(() => {
    reset({
      ...formData,
      ...(isTypesFilter && { severity: Object.values(Severity), activityOption: ActivityOptions.EVERYTHING }),
      ...(isLabelsFilter && { labelIds: [] }),
      ...(isGroupsFilter && { groupIds: [], isCombinedGroupsFilter: false }),
    });
  }, [reset, formData, isTypesFilter, isLabelsFilter, isGroupsFilter]);

  const onReset = async () => {
    const updatedFields = {
      ...(isTypesFilter && { severity: Object.values(Severity), activityOption: ActivityOptions.EVERYTHING }),
      ...(isLabelsFilter && { labelIds: [] }),
      ...(isGroupsFilter && { groupIds: [], isCombinedGroupsFilter: false }),
    };

    resetForm();

    const mergedFilter = { ...filter, ...updatedFields };
    await setActivityListFilter(mergedFilter);
    goBack();
  };

  const onSubmit = async (data: any) => {
    await setActivityListFilter(
      new ActivityFilterModel({ ...filter, ...data })
    );
    goBack();
  };

  const onPressColorFilter = (
    color: string,
    value: (string | undefined)[] | null | undefined,
    onChange: (...event: any[]) => void
  ) => {
    let newValue = [];
    if (!value || value.length === 0) {
      newValue = [color];
    } else if (!value.includes(color)) {
      newValue = value.concat(color);
    } else {
      newValue = value.filter((c: string | undefined) => c !== color);
    }
    onChange(newValue);
  };

  const canReset = useCallback(() => {
    const orderBy = formData.orderBy;
    const assetName = formData.assetName;
    const severity = formData.severity;
    const groupIds = formData.groupIds;
    const labelIds = formData.labelIds;
    const activityOption = formData.activityOption;
    const isCombinedGroupsFilter = formData.isCombinedGroupsFilter;
    const defaultFilter = new ActivityFilterModel();

    const undefinedIfEmpty = (value?: (string | undefined)[] | null) => {
      return value?.length === 0 ? undefined : value;
    };

    return (
      orderBy !== defaultFilter.orderBy ||
      assetName !== defaultFilter.assetName ||
      undefinedIfEmpty(severity) !== defaultFilter.severity ||
      undefinedIfEmpty(groupIds) !== defaultFilter.groupIds ||
      undefinedIfEmpty(labelIds) !== defaultFilter.labelIds ||
      isCombinedGroupsFilter !== defaultFilter.isCombinedGroupsFilter ||
      activityOption !== defaultFilter.activityOption
    );
  }, [formData]);

  useEffect(() => {
    if (!filter) {
      resetForm();
    }
  }, [filter, resetForm]);

  const getFlagButtonLabel = (color: string) => {
    switch (color) {
      case Severity.Red:
        return t('activity.filter.urgent');
      case Severity.Green:
        return t('activity.filter.solved');
      case Severity.Yellow:
        return t('activity.filter.pending');
      case Severity.Purple:
        return t('activity.filter.notes');
      case Severity.Blue:
        return t('activity.filter.routines');
      default:
        return '';
    }
  };

  const renderActivityOptions = () => (
    <View>
      <Text variant="titleMedium" style={styles.filterRowHeader}>
        {t('activity.filter.activityOptions')}
      </Text>
      
      <Controller
        control={control}
        name={'activityOption'}
        render={({ field: { onChange, value } }) => (
          <View style={styles.markersContainer}>
            {activityOptions.map((option, index) => (
              <Pressable key={option.value} onPress={() => onChange(option.value)}>
                <View style={[styles.markerItemRow]}>
                  <View style={[styles.markerLeft, { flexDirection: 'row', alignItems: 'center' }]}>
                    {option.icon && (
                      <MaterialCommunityIcons
                        name={option.icon as any}
                        size={24}
                        color={Colors.role.textStrong}
                        style={{ marginRight: 8 }}
                      />
                    )}
                    <Text style={styles.markerLabel}>{option.label}</Text>
                  </View>
                  <View>
                    <RadioButton.Android
                      value={option.value}
                      status={value === option.value ? 'checked' : 'unchecked'}
                      onPress={() => onChange(option.value)}
                      color={selectionCheckedColor}
                      uncheckedColor={Colors.role.fillMuted}
                    />
                  </View>
                </View>
                {index < 3 && (
                  <View
                    style={{
                      borderBottomWidth: 1,
                      borderColor: Colors.role.borderControl,
                      marginHorizontal: 12,
                    }}
                  />
                )}
              </Pressable>
            ))}
          </View>
        )}
      />
    </View>
  );

  const renderColorFlags = () => (
    <View>
      <Text variant="titleMedium" style={styles.filterRowHeader}>
        {t('activity.filter.activityTypes')}
      </Text>
      <Controller
        control={control}
        name={'severity'}
        render={({ field: { onChange, value } }) => (
          <View style={styles.markersContainer}>
            {Object.entries(SeverityIcons).map(([color, icon]) => (
              <Pressable
                key={color}
                onPress={() => onPressColorFilter(color, value, onChange)}
              >
                <View style={[styles.markerItemRow]}>
                  <View style={styles.markerLeft}>
                    <View
                      style={[
                        styles.colorButton,
                        { backgroundColor: Colors[color as keyof typeof Colors] as string },
                      ]}
                    >
                      <MaterialCommunityIcons
                        size={16}
                        style={{ color: Colors.white }}
                        name={icon}
                      />
                    </View>

                    <Text style={styles.markerLabel}>
                      {getFlagButtonLabel(color)}
                    </Text>
                  </View>
                  <Checkbox.Item
                    label=""
                    status={value?.includes(color) ? 'checked' : 'unchecked'}
                    style={[
                      {
                        borderWidth: 0,
                        padding: 0,
                      },
                    ]}
                    onPress={() => onPressColorFilter(color, value, onChange)}
                    rippleColor={isWeb ? 'transparent' : undefined}
                    uncheckedColor={Colors.role.fillMuted}
                    color={selectionCheckedColor}
                    mode="android"
                  />
                </View>
                <View
                  style={{
                    borderBottomWidth: color !== Severity.Blue ? 1 : 0,
                    borderColor: Colors.role.borderControl,
                    marginHorizontal: 12,
                  }}
                />
              </Pressable>
            ))}
          </View>
        )}
      />
    </View>
  );

  const renderGroups = () => (
    <View style={styles.groupsSection}>
      <GroupFilter
        fieldName="groupIds"
        headerStyle={styles.filterRowHeader}
        workspaceId={workspace?.id}
      />
    </View>
  );

  return (
    <FormContext.Provider value={formMethods}>
      {!isWeb && (
        <FilterHeader canReset={canReset} onReset={onReset} goBack={goBack} />
      )}
      <View style={styles.container}>
        <View
          style={[
            styles.scrollViewContainer,
            isWeb ? { height: 416 } : { marginBottom: 30 },
          ]}
        >
          {isGroupsFilter && (
            <SwitchToggle
              fieldName="isCombinedGroupsFilter"
              label={t('assets.filter.filterToggle')}
            />
          )}
          <ScrollView
            contentContainerStyle={styles.scrollViewContent}
            scrollEnabled={true}
            showsVerticalScrollIndicator={true}
          >
            {isTypesFilter && renderColorFlags()}
            {isTypesFilter && renderActivityOptions()}
            {isGroupsFilter && renderGroups()}
          </ScrollView>
        </View>

        {isWeb ? (
          <DropdownFooter
            isDirty={isDirty}
            onSubmit={handleSubmit(onSubmit)}
            canReset={canReset}
            onReset={onReset}
          />
        ) : (
          <ApplyButton isDirty={isDirty} onSubmit={handleSubmit(onSubmit)} />
        )}
      </View>
    </FormContext.Provider>
  );
};